// src/services/subscriptionService.ts
import apiClient from "../api/apiClient";

const TENANT_ID = process.env.REACT_APP_TENANT_ID ?? "1";

export interface GrantSubscriptionDto {
    userId: number;
    subjectIds: number[];
    expiryDate: string; // ISO format preferred e.g. "2025-12-31T23:59:59Z"
}

/**
 * Manually grant a user access to specific subjects.
 * Endpoint: POST /api/subscriptions/admin/grant
 */
export async function grantSubscription(payload: GrantSubscriptionDto) {
    const resp = await apiClient.post("/subscriptions/admin/grant", payload, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export interface RevokeSubscriptionDto {
    userId: number;
    subjectIds: number[];
}

/**
 * Revoke (cancel) a user's access to specific subjects.
 * Endpoint: POST /api/subscriptions/admin/revoke
 */
export async function revokeSubscription(payload: RevokeSubscriptionDto) {
    const resp = await apiClient.post("/subscriptions/admin/revoke", payload, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export interface AdminSubscription {
    subscriptionId: number;
    subjectId: number;
    name: string;
    medium?: string | null;
    yearlyPrice: number;
    expiry?: string | null; // ISO-8601 instant, or null
    expired: boolean;
}

/**
 * List ALL subscriptions (active + expired) for a user, with the subscription
 * row id needed to edit/delete a specific one.
 * Endpoint: GET /api/subscriptions/admin/user/{userId}
 */
export async function getUserSubscriptions(userId: number): Promise<AdminSubscription[]> {
    const resp = await apiClient.get(`/subscriptions/admin/user/${userId}`, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

/**
 * Update the expiry date of a single subscription.
 * Endpoint: PUT /api/subscriptions/admin/{subscriptionId}/expiry
 */
export async function updateSubscriptionExpiry(subscriptionId: number, expiryDate: string) {
    const resp = await apiClient.put(`/subscriptions/admin/${subscriptionId}/expiry`,
        { expiryDate: new Date(expiryDate).toISOString() },
        { params: { tenantId: TENANT_ID } }
    );
    return resp.data;
}

/**
 * Soft-delete a single subscription (typically an expired one).
 * Endpoint: DELETE /api/subscriptions/admin/{subscriptionId}
 */
export async function deleteSubscription(subscriptionId: number) {
    const resp = await apiClient.delete(`/subscriptions/admin/${subscriptionId}`, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}